import { RANK_CONFIG } from "@/src/constants/post.constant";
import { useAuth } from "@/src/contexts/auth.context";
import { RankedLeaderboardEntry } from "@/src/models/leaderboard.model";
import { useGetRankedLeaderboardQuery } from "@/src/services/api/leaderboard/leaderboard.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { MedalStar, Ranking } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";

type LeaderboardWidgetProps = Readonly<{
  onSeeAll?: () => void;
}>;

type TopUserCardProps = Readonly<{
  entry: RankedLeaderboardEntry;
}>;

const TopUserCard = ({ entry }: TopUserCardProps) => {
  const config = RANK_CONFIG[entry.rank as keyof typeof RANK_CONFIG];
  const formattedPoints = entry?.points?.toLocaleString() ?? "0";

  if (!config) return null;

  const avatarSize = config.avatarSize;

  return (
    <View className="flex-1 items-center justify-end px-1 min-w-0">
      <BlocksAvatar
        userId={entry?.userId}
        fallbackImageUrl={entry?.avatar}
        name={entry?.displayName}
        size={avatarSize}
        className="border-2 border-ash-3"
      />
      <Text
        className="text-xs font-semibold text-onyx text-center mt-1.5 mb-0.5 w-full"
        numberOfLines={1}
      >
        {entry?.displayName}
      </Text>
      <Text className="text-xs text-slate mb-1.5" numberOfLines={1}>
        {formattedPoints}
      </Text>
      <View className={config.badgeClass}>
        <Text className={config.rankTextClass}>{entry?.rank}</Text>
      </View>
    </View>
  );
};

export const LeaderboardWidget = ({ onSeeAll }: LeaderboardWidgetProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { user } = useAuth();
  const currentUserId = user?.itemId ?? "";

  const { data: rankedEntries = [], isLoading } =
    useGetRankedLeaderboardQuery();

  const top3 = rankedEntries.filter((e) => e.rank <= 3);
  const podiumOrder = [
    top3.find((e) => e.rank === 1),
    top3.find((e) => e.rank === 2),
    top3.find((e) => e.rank === 3),
  ].filter(Boolean) as RankedLeaderboardEntry[];

  const currentUser =
    rankedEntries.find((e) => e.userId === currentUserId) ?? null;
  const currentUserPoints = currentUser?.points?.toLocaleString() ?? "0";
  const currentUserRank = currentUser?.rank ?? 0;

  return (
    <View className="rounded-2xl border border-ash-3 bg-chalk p-4">
      {/* Header */}
      <View className="flex-row items-center justify-between mb-4 -mx-4 -mt-4 px-4 py-3 bg-primary-1 rounded-t-2xl">
        <Text className="text-sm font-bold text-primary">
          {t("LABEL_LEADERBOARD")}
        </Text>
        <Pressable
          onPress={onSeeAll}
          className="px-2 py-1 active:opacity-70 web:cursor-pointer hover:bg-ash-3 hover:rounded-lg"
        >
          <Text className="text-xs text-primary">{t("LABEL_SEE_ALL")}</Text>
        </Pressable>
      </View>
      {/* Loading */}
      {isLoading && (
        <View className="items-center py-6">
          <ActivityIndicator size="small" />
        </View>
      )}

      {/* Top 3 podium */}
      {!isLoading && podiumOrder.length > 0 && (
        <View className="flex-row justify-between items-end px-1 mb-4 overflow-hidden">
          {podiumOrder.map((podiumEntry) => (
            <TopUserCard key={podiumEntry.rank} entry={podiumEntry} />
          ))}
        </View>
      )}

      {!isLoading && (
        <>
          <View className="h-px bg-ash-3 mb-3" />

          {/* Current user row */}
          {currentUser ? (
            <View className="flex-row items-center justify-between bg-ash border border-ash-3 rounded-xl p-3 gap-2">
              {/* Avatar + name */}
              <View className="flex-row items-center gap-2 flex-1 min-w-0">
                <BlocksAvatar
                  userId={currentUser.userId}
                  fallbackImageUrl={currentUser.avatar}
                  name={currentUser.displayName}
                  size={36}
                  className="shrink-0"
                />
                <Text
                  className="text-sm font-semibold text-onyx flex-1"
                  numberOfLines={1}
                >
                  {t("LABEL_YOU")}
                </Text>
              </View>

              {/* Stats */}
              <View className="flex-row items-center gap-3 shrink-0">
                {/* Points */}
                <View className="items-start">
                  <Text className="text-xs text-slate">
                    {t("LABEL_COL_POINTS")}
                  </Text>
                  <View className="flex-row items-center gap-1">
                    <Text className="text-sm font-bold text-onyx">
                      {currentUserPoints}
                    </Text>
                    <MedalStar
                      size={16}
                      color={themeVars["--color-primary-default"]}
                      variant="Bold"
                    />
                  </View>
                </View>

                {/* Divider */}
                <View className="w-px h-8 bg-ash-3" />

                {/* Rank */}
                <View className="items-start">
                  <Text className="text-xs text-slate">{t("LABEL_RANK")}</Text>
                  <View className="flex-row items-center gap-1">
                    <Text className="text-sm font-bold text-onyx">
                      #{currentUserRank}
                    </Text>
                    <Ranking
                      size={16}
                      color={themeVars["--color-primary-default"]}
                      variant="Bold"
                    />
                  </View>
                </View>
              </View>
            </View>
          ) : (
            <View className="items-center py-2">
              <Text className="text-xs text-slate">
                {t("LABEL_NO_LEADERBOARD_DATA")}
              </Text>
            </View>
          )}
        </>
      )}
    </View>
  );
};

export default LeaderboardWidget;
